import {
  FilterAll,
  SettlementBillingType,
  SettlementStatus,
  WalletChain,
  WalletType,
  type SettlementStatement,
} from '@/api/settlements'

export { FilterAll, SettlementBillingType, SettlementStatus, WalletChain, WalletType }

export const WithdrawError = {
  AmountRequired: 'amountPh',
  OverBalance: 'overBalance',
  ChainRequired: 'chainRequired',
  AddressInvalid: 'addressInvalid',
} as const
export type WithdrawError = (typeof WithdrawError)[keyof typeof WithdrawError]

export const WITHDRAW_I18N = {
  [WithdrawError.AmountRequired]: 'settlements.amountPh',
  [WithdrawError.OverBalance]: 'settlements.overBalance',
  [WithdrawError.ChainRequired]: 'settlements.chainRequired',
  [WithdrawError.AddressInvalid]: 'settlements.addressInvalid',
} as const

export const SETTLEMENT_STATUS_I18N = {
  [SettlementStatus.Pending]: 'settlements.pending',
  [SettlementStatus.Confirmed]: 'settlements.confirmed',
  [SettlementStatus.Rejected]: 'settlements.rejected',
  [SettlementStatus.Closed]: 'settlements.closed',
} as const

export const SETTLEMENT_BILLING_I18N = {
  [SettlementBillingType.Workspace]: 'settlements.workspace',
  [SettlementBillingType.Seat]: 'settlements.seat',
  [SettlementBillingType.Usage]: 'settlements.usage',
  [SettlementBillingType.Module]: 'settlements.module',
} as const

export const WALLET_I18N = {
  [WalletType.Hibro]: 'settlements.hibro',
  [WalletType.External]: 'settlements.external',
} as const

export const CHAIN_I18N = {
  [WalletChain.Ethereum]: 'settlements.ethereum',
  [WalletChain.Bnb]: 'settlements.bnb',
  [WalletChain.Tron]: 'settlements.tron',
} as const

export const SHANGHAI_TZ = 'Asia/Shanghai'

export function formatCny(n: number, keepCents = false) {
  const abs = Math.abs(n)
  const [int, dec] = abs.toFixed(2).split('.')
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  const trimmed = dec.replace(/0+$/, '')
  const body = keepCents ? `${grouped}.${dec}` : trimmed ? `${grouped}.${trimmed}` : grouped
  return `${n < 0 ? '-' : ''}¥${body}`
}

export function shanghaiToday(now = Date.now()) {
  return new Date(now).toLocaleDateString('en-CA', { timeZone: SHANGHAI_TZ })
}

export function isServiceOngoing(end: string, now = Date.now()) {
  return shanghaiToday(now) <= end
}

export function formatConfirmAt(iso: string) {
  return new Date(iso)
    .toLocaleString('zh-CN', { timeZone: SHANGHAI_TZ, hour12: false })
    .replace(/\//g, '-')
}

export function acceptAmount(raw: string) {
  return raw === '' || /^\d+(\.\d{0,2})?$/.test(raw)
}

export function sanitizeAddress(raw: string) {
  return raw.replace(/[^A-Za-z0-9]/g, '').slice(0, 128)
}

export function allBalanceInput(cap: number) {
  return formatCny(cap).replace(/[¥,]/g, '')
}

export type StatementFilters = {
  status: SettlementStatus | ''
  billing: SettlementBillingType | ''
  from: string
  to: string
}

export function filterStatements(rows: SettlementStatement[], filters: StatementFilters) {
  return rows.filter((row) => {
    if (filters.status && row.status !== filters.status) return false
    if (filters.billing && row.billing_type !== filters.billing) return false
    if (filters.from && row.settlement_date < filters.from) return false
    if (filters.to && row.settlement_date > filters.to) return false
    return true
  })
}

export type WithdrawErrors = {
  amount?: typeof WithdrawError.AmountRequired | typeof WithdrawError.OverBalance
  chain?: typeof WithdrawError.ChainRequired
  address?: typeof WithdrawError.AddressInvalid
}

export function validateWithdraw(input: {
  amount: string
  cap: number
  wallet: WalletType
  chain?: WalletChain
  address: string
}): WithdrawErrors {
  const n = Number(input.amount)
  const next: WithdrawErrors = {}
  if (!input.amount || !Number.isFinite(n) || n <= 0) next.amount = WithdrawError.AmountRequired
  else if (n > input.cap) next.amount = WithdrawError.OverBalance
  if (input.wallet === WalletType.External) {
    if (!input.chain) next.chain = WithdrawError.ChainRequired
    if (!input.address || !/^[A-Za-z0-9]{1,128}$/.test(input.address)) {
      next.address = WithdrawError.AddressInvalid
    }
  }
  return next
}

export function hasWithdrawErrors(errors: WithdrawErrors) {
  return Boolean(errors.amount || errors.chain || errors.address)
}
